// Keep the types and their details in sync with haazri-api/constants/assets.js.
const COLOR = { key: "color", label: "Colour" };

export const ASSET_TYPES = [
  {
    value: "laptop",
    label: "Laptop",
    icon: "laptop_mac",
    specs: [
      { key: "processor", label: "Processor", placeholder: "e.g. Apple M2" },
      { key: "memory", label: "Memory", placeholder: "e.g. 16 GB" },
      { key: "graphics", label: "Graphics", placeholder: "e.g. 8C CPU, 10C GPU" },
      { key: "storage", label: "Storage", placeholder: "e.g. 1 TB" },
      COLOR,
      { key: "battery_cycle_count", label: "Battery cycle count" },
      { key: "device_name", label: "Device name", placeholder: "e.g. DESKTOP-CD6HPFE" },
      { key: "product_id", label: "Product ID", placeholder: "Windows laptops" },
      {
        key: "secondary_serial",
        label: "Other serial / ID",
        placeholder: "e.g. back of device, or the original",
      },
    ],
  },
  {
    value: "phone",
    label: "Phone",
    icon: "smartphone",
    specs: [
      { key: "model_number", label: "Model number" },
      { key: "ram", label: "RAM", placeholder: "e.g. 4GB" },
      { key: "storage", label: "Storage", placeholder: "e.g. 64GB" },
      COLOR,
      { key: "battery_health", label: "Battery health", placeholder: "e.g. 82%" },
    ],
    hasImeis: true,
    hasPta: true,
  },
  {
    value: "tablet",
    label: "Tablet",
    icon: "tablet_mac",
    specs: [
      { key: "model_number", label: "Model number" },
      { key: "ram", label: "RAM", placeholder: "e.g. 4GB" },
      { key: "storage", label: "Storage", placeholder: "e.g. 64GB" },
      COLOR,
      { key: "battery_health", label: "Battery health", placeholder: "e.g. 82%" },
    ],
    hasImeis: true,
  },
  { value: "charger", label: "Charger", icon: "power", specs: [] },
  { value: "cable", label: "Cable", icon: "cable", specs: [] },
  { value: "other", label: "Other", icon: "devices_other", specs: [] },
];

export const PTA_STATUSES = [
  { value: "", label: "Not set" },
  { value: "pta", label: "PTA approved" },
  { value: "non_pta", label: "Non-PTA" },
];

// What an asset is right now. "assigned" and "in_stock" are worked out by the
// API from the assignments; the rest are set by hand or by disposing of it.
export const ASSET_STATES = [
  { value: "in_stock", label: "In stock", color: "success" },
  { value: "assigned", label: "Assigned", color: "info" },
  { value: "in_repair", label: "In repair", color: "warning" },
  { value: "disposed", label: "Disposed", color: "secondary" },
];

// Statuses HR can set when editing. Disposed only comes from disposing.
export const EDITABLE_STATUSES = [
  { value: "active", label: "Active" },
  { value: "in_repair", label: "In repair" },
];

// How an asset leaves the company. Keep in sync with the API's constants.
export const DISPOSAL_REASONS = [
  { value: "severance", label: "Given as severance" },
  { value: "sold", label: "Sold" },
  { value: "donated", label: "Donated" },
  { value: "lost", label: "Lost" },
  { value: "stolen", label: "Stolen" },
  { value: "scrapped", label: "Scrapped / used for parts" },
  { value: "dead", label: "Dead / beyond repair" },
  { value: "other", label: "Other" },
];

export const END_REASON_LABELS = {
  returned: "Returned",
  ...Object.fromEntries(DISPOSAL_REASONS.map(({ value, label }) => [value, label])),
};

// Reasons where the asset goes to a person (so it shows on their record).
export const GIVEN_TO_PERSON_REASONS = ["severance", "sold"];

export const ASSETS_COLUMNS = [
  { name: "asset", align: "left" },
  { name: "type / property of", align: "left" },
  { name: "assigned to", align: "left" },
  { name: "status", align: "center" },
  { name: "purchased", align: "left" },
  { name: "actions", align: "center" },
];

export const ASSETS_MESSAGES = {
  empty: "No assets found.",
  nameRequired: "Model / name is required.",
  employeeRequired: "Select an employee.",
  assetRequired: "Select an asset.",
  priceInvalid: "Enter a valid amount.",
  returnBeforeAssigned: "The date can't be before the asset was assigned.",
  created: "Asset added successfully.",
  updated: "Asset updated successfully.",
  deleted: "Asset deleted successfully.",
  assigned: "Asset assigned successfully.",
  returned: "Asset returned successfully.",
  disposed: "Asset disposed successfully.",
};

export const typeOf = (value) => ASSET_TYPES.find((type) => type.value === value);

export const stateOf = (value) => ASSET_STATES.find((state) => state.value === value);

export const DEFAULT_CURRENCY = "PKR";

// To offer another currency, add its ISO 4217 code here (the API accepts any).
export const CURRENCIES = ["PKR", "USD", "EUR", "GBP", "AED", "SAR"].map((code) => ({
  value: code,
  label: code,
}));

export const formatMoney = (amount, currency = DEFAULT_CURRENCY) =>
  amount === null || amount === undefined
    ? "—"
    : `${currency} ${Number(amount).toLocaleString("en-PK")}`;

export const personName = (user) => user?.name || user?.user_name || "—";

// Text that searches match against.
export const searchableText = (asset) =>
  [
    asset.name,
    asset.serial_number,
    asset.purchased_by,
    asset.specs?.model_number,
    ...(asset.specs?.imeis || []),
    personName(asset.current_assignment?.user),
    asset.property_of,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

// Splits the assets by person: what they currently hold, and what the company
// gave them as severance or sold them.
export const groupAssetsByUser = (assets) => {
  const byUser = {};
  const bucket = (id) => {
    byUser[id] = byUser[id] || { held: [], received: [] };
    return byUser[id];
  };
  assets.forEach((asset) => {
    if (asset.current_assignment) {
      bucket(asset.current_assignment.fk_user_id).held.push(asset);
    } else if (
      asset.status === "disposed" &&
      GIVEN_TO_PERSON_REASONS.includes(asset.disposal_reason) &&
      asset.assignments[0]
    ) {
      bucket(asset.assignments[0].fk_user_id).received.push(asset);
    }
  });
  return byUser;
};

// Amount fields are plain text inputs (no spinner arrows, and the mouse wheel
// can't change them) that only accept digits and one decimal point.
export const toAmountText = (text) => text.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
